import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useContracts } from '../../hooks/useContract';
import { usePackageTiers } from '../../hooks/usePackageTiers';
import { useXtPrice } from '../../hooks/useXtPrice';
import { useNotify } from '../../context/NotificationContext';
import { formatUSDT } from '../../utils/format';
import { Spinner } from '../common/Loader';
import MachineCard from '../invest/MachineCard';
import MachineDetailModal from '../invest/MachineDetailModal';
import '../invest/Machines.css';
import './Register.css';

export default function RegisterForm({ balances, onDone }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { tiers, loading: tiersLoading } = usePackageTiers();
  const { xtPrice } = useXtPrice();
  const { write, read } = useContracts();
  const notify = useNotify();

  const initialSponsor = searchParams.get('ref') || '';
  const [sponsorId, setSponsorId] = useState(initialSponsor);
  const [openIdx, setOpenIdx] = useState(null);
  const [sponsorValid, setSponsorValid] = useState(null);

  useEffect(() => {
    if (!sponsorId) { setSponsorValid(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const user = await read.storage.getUser(sponsorId);
        if (!cancelled) setSponsorValid(user.exists);
      } catch {
        if (!cancelled) setSponsorValid(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sponsorId]);

  function tryOpen(idx) {
    if (sponsorId && sponsorValid === false) {
      notify.error('Invalid sponsor ID — fix it before selecting a machine.');
      return;
    }
    setOpenIdx(idx);
  }

  // registerAndInvest(sponsorId, tierIndex) — no position anymore
  async function registerAction(tierIndex) {
    const sponsor = sponsorId ? sponsorId : 0;
    return write.logic.registerAndInvest(sponsor, tierIndex);
  }

  const openTier = openIdx !== null ? tiers[openIdx] : null;
  const canProceed = !(sponsorId && sponsorValid === false);

  return (
    <div>
      {/* Sponsor */}
      <div className="card register-card" style={{ maxWidth: 720, margin: '0 auto 24px' }}>
        <h2 className="register-title">Create Your <span className="text-gradient">Mining Account</span></h2>
        <p className="register-subtitle">Enter your sponsor, then choose a mining machine.</p>

        <div className="mb-6">
          <label className="label">Sponsor ID (optional)</label>
          <input
            className="input"
            type="text"
            placeholder="e.g. 5000"
            value={sponsorId}
            onChange={(e) => setSponsorId(e.target.value.replace(/\D/g, ''))}
          />
          <div style={{ marginTop: 6, fontSize: 12 }}>
            {sponsorId === '' && <span className="text-muted">Leave blank to use the default sponsor.</span>}
            {sponsorValid === true && <span style={{ color: 'var(--success)' }}>✓ Valid sponsor</span>}
            {sponsorValid === false && <span style={{ color: 'var(--error)' }}>✕ Sponsor not found</span>}
          </div>
        </div>
      </div>

      {/* Machine selection */}
      <div style={{ marginBottom: 16 }}>
        <div className="section-eyebrow">SELECT YOUR MACHINE</div>
        <h2>Choose an <span className="text-gradient">XTerra Forge</span></h2>
        <p style={{ color: 'var(--text-muted)', marginTop: 6, fontSize: 14 }}>
          Click a machine to review details and register in one transaction.
        </p>
      </div>

      <div className="balance-strip">
        <div className="item">USDT Balance <strong>{formatUSDT(balances.usdt)}</strong></div>
        <div className="item">XT Balance <strong>{formatUSDT(balances.xt, 4)}</strong></div>
        <div className="item">
          USDT Allowance{' '}
          <strong style={{ color: balances.allowance.gt(0) ? 'var(--accent)' : 'var(--warning)' }}>
            {balances.allowance.gt(ethers.utils.parseUnits('1000000000', 18))
              ? '∞ Unlimited'
              : formatUSDT(balances.allowance)}
          </strong>
        </div>
      </div>

      {tiersLoading && !tiers.length ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spinner size="lg" /></div>
      ) : (
        <div className="machine-grid">
          {tiers.map((tier, i) => (
            <MachineCard
              key={i}
              tier={tier}
              index={i}
              selected={openIdx === i}
              onOpen={tryOpen}
            />
          ))}
        </div>
      )}

      {openTier && (
        <MachineDetailModal
          tierIndex={openIdx}
          tier={openTier}
          balances={balances}
          xtPrice={xtPrice}
          onClose={() => setOpenIdx(null)}
          onDone={() => {
            if (onDone) onDone();
            navigate('/dashboard');
          }}
          isRegistered={true}
          requireRegistered={false}
          actionFn={registerAction}
          actionLabel="Register & Start Mining"
          actionDisabled={!canProceed}
          successMsg="Registered! Welcome to X-Terra Mining."
        />
      )}
    </div>
  );
}
